import { getFontCss, styleConstants } from '@paywithextend/components'
import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import {
  selectAdminsAndOwnersFromState,
  selectPatchingOrganizationApproverFromState,
} from 'src/app/BusinessRules/redux/businessRules.selectors'
import AmountInputField from 'src/components/AmountInputField/AmountInputField'
import Button, {
  ButtonDesigns,
  ButtonTypes,
} from 'src/components/Button/Button'
import ErrorList from 'src/components/ErrorList/ErrorList'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import OnOffSwitch from 'src/components/OnOffSwitch/OnOffSwitch'
import UserDropdown, {
  UserDropdownInfiniteScrollProps,
} from 'src/components/UserDropdown/UserDropdown'
import { ExtendCurrency } from 'src/enums'
import {
  FormFieldsValidation,
  useBackgroundDispatch,
  useFormFields,
} from 'src/hooks'
import Trns, { translateSingleString } from 'src/localization/Trns'
import {
  ApprovalRuleResponse,
  ApprovalRuleType,
  CardApprovalBody,
  OrganizationsListModel,
} from 'src/models/organizations.models'
import { UserListModel } from 'src/models/user.models'
import {
  deleteOrganizationCardApprovalsEnabled,
  postOrganizationCardApprovalsEnabled,
} from 'src/redux/actions/data/organizations.actions'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import { selectMembersListModelsFromState } from 'src/redux/selectors/data/members.selectors'
import { selectOrganizationTopLevelErrorFromState } from 'src/redux/selectors/data/organizations.selectors'
import { toCents, toDollars } from 'src/utils/currency.utils'
import { stringIsEmpty } from 'src/utils/validation/string.validation'
import { css } from 'styled-components'

export type CardApprovalFields = {
  approverId?: string
  type: ApprovalRuleType
  isVisible: boolean
  usdThresholdCents: number
  cadThresholdCents: number
}

interface CardApprovalsFormProps {
  closeCardApprovalsForm: () => void
  userInfiniteScrollProps: UserDropdownInfiniteScrollProps
  organization: OrganizationsListModel
  approver?: UserListModel
  error?: string
  cardApprovalRules?: ApprovalRuleResponse
  cardApprovalsEnabled: boolean
}

const CardApprovalsForm: React.FC<CardApprovalsFormProps> = (
  props,
): React.ReactElement => {
  const dispatch = useBackgroundDispatch()
  const locale = useSelector(selectNullSafeLocaleFromState)
  const adminsAndOwners = useSelector(selectAdminsAndOwnersFromState)
  const loading = useSelector(selectPatchingOrganizationApproverFromState)
  const [showWarningMessage, setShowWarningMessage] = useState<boolean>(false)
  const [validationError, setValidationError] = useState<string>('')
  const { analytics } = useAnalyticsContext()
  const users = useSelector(selectMembersListModelsFromState)
  const topLevelError = useSelector(selectOrganizationTopLevelErrorFromState)

  const {
    closeCardApprovalsForm: closeCardApprovalRuleForm,
    userInfiniteScrollProps,
    organization,
    approver,
  } = props

  const cardApprovalRuleFieldsValidation =
    (): FormFieldsValidation<CardApprovalFields> => ({
      approverId: [stringIsEmpty()],
    })

  const {
    fields: cardApprovalRuleFlatFields,
    onFieldChange: cardApprovalRuleFlatFieldChange,
    fieldsErrors: cardApprovalRuleFlatFieldsErrors,
    setErrorsAndReturnDidPassValidation: flatFieldsSetErrors,
  } = useFormFields<CardApprovalFields>(
    {
      approverId: approver?.id,
      type: ApprovalRuleType.Approver,
      isVisible: props.cardApprovalsEnabled,
      usdThresholdCents: props.cardApprovalRules?.thresholds.usdCents ?? 0,
      cadThresholdCents: props.cardApprovalRules?.thresholds.cadCents ?? 0,
    },
    cardApprovalRuleFieldsValidation,
  )

  const hasCurrencyInOrg = (inputField: string): boolean => {
    let hasCurrencyInOrg = false
    props.organization?.currencies?.some((currency) => {
      if (currency.inputField === inputField) {
        hasCurrencyInOrg = true
      }
    })

    return hasCurrencyInOrg
  }

  const selectedMember = cardApprovalRuleFlatFields.approverId
    ? new Set<string>([cardApprovalRuleFlatFields.approverId])
    : new Set<string>()

  const handleOnCancel = (): void => {
    closeCardApprovalRuleForm()
  }

  const handleOnSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault()
    const cardApprovalRules: CardApprovalBody = {
      approverId: cardApprovalRuleFlatFields.approverId ?? '',
      type: cardApprovalRuleFlatFields.type,
      thresholds: {
        usdCents: cardApprovalRuleFlatFields.usdThresholdCents,
        cadCents: cardApprovalRuleFlatFields.cadThresholdCents,
      },
    }
    if (topLevelError) {
      setValidationError(topLevelError)
    }
    if (flatFieldsSetErrors() && cardApprovalRuleFlatFields.isVisible) {
      dispatch(
        postOrganizationCardApprovalsEnabled(
          organization.id,
          cardApprovalRules,
          closeCardApprovalRuleForm,
        ),
      )
      analytics?.track(
        !props.cardApprovalsEnabled
          ? 'enable card approvals'
          : 'update card approvals',
        {
          card_approver_id: cardApprovalRules.approverId,
          card_approver_role:
            users[
              cardApprovalRules.approverId
            ]?.orgRoleDisplayTagTitle.toLowerCase(),
          threshold_value_USD: toDollars(
            cardApprovalRules.thresholds.usdCents,
          ).toFixed(2),
          threshold_value_CAD: toDollars(
            cardApprovalRules.thresholds.cadCents,
          ).toFixed(2),
        },
      )
    } else if (
      !cardApprovalRuleFlatFields.isVisible &&
      props.cardApprovalRules?.id
    ) {
      dispatch(
        deleteOrganizationCardApprovalsEnabled(
          organization.id,
          props.cardApprovalRules?.id,
          closeCardApprovalRuleForm,
        ),
      )
      analytics?.track('disable card approvals')
    } else if (!flatFieldsSetErrors() && cardApprovalRuleFlatFields.isVisible) {
      setValidationError('Please select an approver')
    }
  }

  return (
    <div>
      <form
        onSubmit={handleOnSubmit}
        css={css`
          display: flex;
          width: 100%;
        `}
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
            width: 100%;
            row-gap: 40px;
          `}
        >
          <span
            css={css`
              ${getFontCss(
                12,
                styleConstants.colors.gray_darkest,
                styleConstants.fontFamilies.regular,
              )}
            `}
          >
            <Trns
              source={
                'Assign an Owner or Admin to approve virtual card requests, updates, and creations across your organization.'
              }
            />
          </span>
          <div
            css={css`
              display: flex;
              flex-direction: column;
            `}
          >
            <span
              css={css`
                ${getFontCss(
                  14,
                  styleConstants.colors.gray_darkest,
                  styleConstants.fontFamilies.regular,
                )};
                display: flex;
                margin-bottom: 8px;
              `}
            >
              <Trns source={'Card Approver Rule'} />
            </span>
            <div
              css={css`
                display: flex;
                width: 175px;
              `}
            >
              <OnOffSwitch
                defaultOnOffState={props.cardApprovalsEnabled}
                onChange={(val: boolean): void => {
                  cardApprovalRuleFlatFieldChange('isVisible', val)
                  setShowWarningMessage(
                    !val && props.cardApprovalRules !== undefined,
                  )
                  if (!val) setValidationError('')
                }}
                testId={'cardApprovalsSwitch'}
              />
            </div>
          </div>
          {cardApprovalRuleFlatFields.isVisible && (
            <div
              css={css`
                display: flex;
                flex-direction: column;
              `}
            >
              <UserDropdown
                isCreatable={false}
                placeholder={translateSingleString(
                  locale,
                  'Type or select from list',
                )}
                fieldLabel={
                  <span
                    css={css`
                      ${getFontCss(
                        14,
                        styleConstants.colors.gray_darkest,
                        styleConstants.fontFamilies.regular,
                      )};
                      display: flex;
                      align-items: center;
                    `}
                  >
                    <Trns source={'Card Approver'} />
                  </span>
                }
                chooseOption={(val: string): void => {
                  cardApprovalRuleFlatFieldChange('approverId', val)
                }}
                selectedValues={selectedMember}
                optionGroups={[
                  {
                    options: adminsAndOwners,
                  },
                ]}
                infiniteScrollProps={userInfiniteScrollProps}
                allowNone
                css={css`
                  width: 100%;
                  min-height: 110px;
                `}
                data-testid={'cardApprovalsUserDropdown'}
              />
              <>
                <span
                  css={css`
                    ${getFontCss(
                      14,
                      styleConstants.colors.gray_darkest,
                      styleConstants.fontFamilies.regular,
                    )};
                    margin-bottom: 8px;
                  `}
                >
                  <Trns source={'Required for Cards Over'} />
                </span>
                <div
                  css={css`
                    display: flex;
                    flex-direction: column;
                    row-gap: 8px;
                    margin-bottom: 4px;
                  `}
                >
                  {(hasCurrencyInOrg('usdThresholdCents') ||
                    props.organization?.currencies?.length === 0) && (
                    <AmountInputField
                      baseValue={
                        props.cardApprovalRules?.thresholds.usdCents !==
                        undefined
                          ? toDollars(
                              props.cardApprovalRules?.thresholds.usdCents,
                            )
                          : 0
                      }
                      value={toDollars(
                        cardApprovalRuleFlatFields['usdThresholdCents'],
                      )}
                      onChange={(val: number | undefined): void => {
                        cardApprovalRuleFlatFieldChange(
                          'usdThresholdCents',
                          val ? toCents(val) : 0,
                        )
                      }}
                      errors={
                        cardApprovalRuleFlatFieldsErrors['usdThresholdCents']
                      }
                      maxValue={99999999}
                      currency={ExtendCurrency.Usd}
                      isRequest={false}
                      testId={'usdThresholdCardApprovals'}
                    />
                  )}
                  {hasCurrencyInOrg('cadThresholdCents') && (
                    <AmountInputField
                      baseValue={
                        props.cardApprovalRules?.thresholds.cadCents !==
                        undefined
                          ? toDollars(
                              props.cardApprovalRules?.thresholds.cadCents,
                            )
                          : 0
                      }
                      value={toDollars(
                        cardApprovalRuleFlatFields['cadThresholdCents'],
                      )}
                      onChange={(val: number | undefined): void => {
                        cardApprovalRuleFlatFieldChange(
                          'cadThresholdCents',
                          val ? toCents(val) : 0,
                        )
                      }}
                      errors={
                        cardApprovalRuleFlatFieldsErrors['cadThresholdCents']
                      }
                      maxValue={99999999}
                      currency={ExtendCurrency.Cad}
                      isRequest={false}
                      testId={'cadThresholdCardApprovals'}
                    />
                  )}
                </div>
              </>
            </div>
          )}
          <div
            css={css`
              display: flex;
              flex-direction: column;
            `}
          >
            {showWarningMessage && (
              <span
                css={css`
                  ${getFontCss(
                    12,
                    styleConstants.colors.red,
                    styleConstants.fontFamilies.medium,
                  )};
                  margin-bottom: 8px;
                `}
              >
                {
                  <Trns
                    source={
                      'By disabling Card Approvals, all card requests pending final approval will be cancelled'
                    }
                  />
                }
              </span>
            )}
            {(props.error || validationError) && (
              <ErrorList
                errors={[props.error || validationError]}
                css={css`
                  padding: 0px;
                  margin-bottom: 10px;
                `}
              />
            )}
            <div
              css={css`
                display: flex;
                align-items: center;
              `}
            >
              <Button
                type={ButtonTypes.submit}
                design={ButtonDesigns.designCta}
                text={<Trns source={'Save'} />}
                big
                loading={loading}
                css={css`
                  align-self: flex-start;
                `}
                testId={'saveCardApprovalsBtn'}
              />
              <HoverableLink
                ariaLabelSource={'Cancel'}
                onClick={handleOnCancel}
                css={css`
                  margin-left: 20px;
                `}
                testId={'cancelCardApprovalsBtn'}
              >
                <Trns source={'Cancel'} />
              </HoverableLink>
            </div>
          </div>
        </div>
      </form>
    </div>
  )
}

export default CardApprovalsForm
